import React, { useState, useEffect } from "react";
import { leaveAPI } from "../../lib/api";
import { toast } from "sonner";
import { capitalizeFirst } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { CalendarDays } from "lucide-react";

const LeaveBalancePage = () => {
  const [balance, setBalance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBalance();
  }, []);

  const fetchBalance = async () => {
    try {
      const response = await leaveAPI.getBalance();
      setBalance(response.data);
    } catch (error) {
      toast.error("Failed to fetch leave balance");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leave-balance-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Leave Balance</h1>
        <p className="text-slate-500">View your available leave balance</p>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {balance.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="p-8 text-center text-slate-500">
              No leave policies configured. Contact HR.
            </CardContent>
          </Card>
        ) : (
          balance.map((item, index) => {
            const percentage = (item.used / item.annual_quota) * 100;
            return (
              <Card key={index} className="card-hover" data-testid={`balance-card-${item.leave_type}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-lg capitalize">
                      {item.leave_type} Leave
                    </CardTitle>
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <CalendarDays className="w-5 h-5 text-indigo-600" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-4xl font-bold text-slate-900">{item.available}</p>
                        <p className="text-sm text-slate-500">days available</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">of {item.annual_quota} total</p>
                        <p className="text-sm text-slate-500">{item.used} used</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Used</span>
                        <span className="font-medium">{percentage.toFixed(0)}%</span>
                      </div>
                      <Progress value={percentage} className="h-2" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Summary */}
      {balance.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-slate-900">
                  {balance.reduce((acc, b) => acc + b.annual_quota, 0)}
                </p>
                <p className="text-sm text-slate-500">Total Annual</p>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-lg">
                <p className="text-3xl font-bold text-emerald-600">
                  {balance.reduce((acc, b) => acc + b.available, 0)}
                </p>
                <p className="text-sm text-slate-500">Available</p>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <p className="text-3xl font-bold text-amber-600">
                  {balance.reduce((acc, b) => acc + b.used, 0)}
                </p>
                <p className="text-sm text-slate-500">Used</p>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-lg">
                <p className="text-3xl font-bold text-indigo-600">
                  {balance.length}
                </p>
                <p className="text-sm text-slate-500">Leave Types</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LeaveBalancePage;
